import PlacesService from '../services/PlacesService';

export interface GeoJsonUploadOptions {
  geoJsonData: any;
  filename: string;
  userData: any;
}

export interface GeoJsonCacheData {
  geoJson: any;
  metadata: any | null;
}

/**
 * Upload GeoJSON data as a new place
 */
export const uploadGeoJsonAsPlace = async (geoJsonData: any, filename: string, userData: any) => {
  let placeData;

  try {
    // Generate slug from filename (matching Python implementation)
    const name = filename.replace(/\.[^/.]+$/, '');
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    // Add the special io: properties to the GeoJSON features (CRITICAL - this is what Python does)
    const modifiedGeoJson = { ...geoJsonData };

    if (modifiedGeoJson.type === 'FeatureCollection' && modifiedGeoJson.features) {
      modifiedGeoJson.features = modifiedGeoJson.features.map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          'io:name': name,
          'io:slug': slug,
          'io:workspace': userData.organization,
        },
      }));
    } else if (modifiedGeoJson.type === 'Feature') {
      modifiedGeoJson.properties = {
        ...modifiedGeoJson.properties,
        'io:name': name,
        'io:slug': slug,
        'io:workspace': userData.organization,
      };
    }

    // Prepare the place data - matching the API schema for management/locations
    placeData = {
      name: name,
      slug: slug,
      type: 'locality',
      description: '', // Optional but included
      geojson: modifiedGeoJson,
      published: true, // Make the place published so it's visible
    };

    // Use group parameter like Python does
    const queryParams = userData.organization
      ? {
          group: userData.organization,
        }
      : {};

    console.log('Uploading place with data:', {
      placeData,
      queryParams,
      userData: { organization: userData.organization, email: userData.email },
    });

    // Now using the management endpoint via updated PlacesService
    const result = await PlacesService.addPlace(placeData, queryParams);

    console.log('Successfully uploaded place:', result);
    return result;
  } catch (error: any) {
    console.error('Error uploading GeoJSON as place:', error);
    console.error('Error data:', error?.data);
    console.error('Error status:', error?.status);

    // Log detailed error information
    if (error?.data?.errors && Array.isArray(error.data.errors)) {
      console.error('Detailed validation errors:', JSON.stringify(error.data.errors, null, 2));
    }

    // Check for duplicate error (500 + "Could not save document")
    const isDuplicateError =
      error?.status === 500 &&
      error?.data?.error === 'Could not save document.' &&
      error?.data?.message === 'Internal Server Error';

    if (isDuplicateError) {
      // Create a custom error for duplicate detection
      const duplicateError = new Error(
        `A place with the name "${placeData?.name || filename}" already exists in your workspace.`
      ) as any;
      duplicateError.name = 'DuplicateError';
      duplicateError.placeName = placeData?.name || filename.replace(/\.[^/.]+$/, '');
      duplicateError.slug = placeData?.slug;
      throw duplicateError;
    }

    // For non-duplicate errors, enhance the error object with better information
    // Ensure there's a message property for better error handling upstream
    if (!error.message) {
      if (error?.data?.message) {
        error.message = error.data.message;
      } else if (error?.data?.error) {
        error.message = error.data.error;
      } else if (error?.data?.errors && Array.isArray(error.data.errors)) {
        // Build a more descriptive error message including field information
        error.message = error.data.errors
          .map((e: any) => {
            const field = e.source?.pointer || e.source?.parameter || 'unknown field';
            const detail = e.detail || e.title || 'Invalid value';
            return `${field}: ${detail}`;
          })
          .join('; ');
      } else {
        error.message = `Failed to upload GeoJSON: HTTP ${error?.status || 'unknown error'}`;
      }
    }

    throw error;
  }
};

/**
 * Cache GeoJSON data in session storage
 */
export const cacheGeoJsonData = (
  jsonData: any,
  filename: string,
  file: File
): { cacheKey: string; metadata: any } => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const baseName = filename.replace(/\.(geojson|json)$/i, '');
  const cacheKey = `chat-drop-${baseName}-${timestamp}`;

  const cacheMetadata = {
    filename: filename,
    timestamp: new Date().toISOString(),
    size: file.size,
    featureCount: jsonData.type === 'FeatureCollection' ? jsonData.features?.length || 0 : 1,
    originalFileName: filename,
    droppedInChat: true,
  };

  // Store in sessionStorage
  sessionStorage.setItem(cacheKey, JSON.stringify(jsonData));
  sessionStorage.setItem(`${cacheKey}-metadata`, JSON.stringify(cacheMetadata));

  return { cacheKey, metadata: cacheMetadata };
};

/**
 * Retrieve cached GeoJSON data
 */
export const retrieveCachedGeoJson = (cacheKey: string): GeoJsonCacheData | null => {
  try {
    const cachedData = sessionStorage.getItem(cacheKey);
    const cachedMetadata = sessionStorage.getItem(`${cacheKey}-metadata`);

    if (cachedData) {
      return {
        geoJson: JSON.parse(cachedData),
        metadata: cachedMetadata ? JSON.parse(cachedMetadata) : null,
      };
    }
    return null;
  } catch (error) {
    console.error('Error retrieving cached GeoJSON:', error);
    return null;
  }
};

/**
 * Check if JSON data is GeoJSON format
 */
export const isGeoJson = (jsonData: any): boolean => {
  return (
    jsonData.type &&
    (jsonData.type === 'FeatureCollection' ||
      jsonData.type === 'Feature' ||
      jsonData.type === 'GeometryCollection')
  );
};

/**
 * Process a dropped file and handle GeoJSON upload
 */
export const processDroppedGeoJsonFile = async (
  file: File,
  userData: any,
  onSuccess?: (result: any, metadata: any) => void,
  onError?: (error: Error) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (typeof result === 'string') {
          const jsonData = JSON.parse(result);

          console.log('=== Processing Dropped File ===');
          console.log('File name:', file.name);
          console.log('File type:', file.type);
          console.log('File size:', file.size);
          console.log('Parsed data:', jsonData);

          // Cache the data
          const { cacheKey, metadata } = cacheGeoJsonData(jsonData, file.name, file);

          if (isGeoJson(jsonData)) {
            console.log('=== Detected GeoJSON ===');

            try {
              const uploadResult = await uploadGeoJsonAsPlace(jsonData, file.name, userData);
              console.log('Auto-upload successful:', uploadResult);

              onSuccess?.(uploadResult, metadata);
              resolve();
            } catch (uploadError) {
              console.error('Auto-upload failed:', uploadError);
              onError?.(uploadError);
              reject(uploadError);
            }
          } else {
            // Not GeoJSON, but still cached
            console.log('=== JSON file cached (not GeoJSON) ===');
            resolve();
          }
        }
      } catch (error) {
        console.error('Error parsing JSON file:', error);
        onError?.(error);
        reject(error);
      }
    };

    reader.onerror = () => {
      const error = new Error('Failed to read file');
      onError?.(error);
      reject(error);
    };

    reader.readAsText(file);
  });
};

/**
 * Check if a place with the given name/slug already exists
 */
export const checkPlaceExists = async (name: string, userData: any): Promise<any | null> => {
  try {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const queryParams = userData.organization ? { group: userData.organization } : {};

    // Try to get the place by slug
    const places = await PlacesService.fetchPlaces({
      ...queryParams,
      search: slug,
      limit: 50,
    });

    // Look for exact slug match
    const existingPlace = places.data?.find((place) => place.slug === slug || place.name === name);

    return existingPlace || null;
  } catch (error) {
    console.error('Error checking if place exists:', error);
    return null;
  }
};

/**
 * Validate polygon coordinates
 */
export const validateCoordinates = (coordinates: any[]): boolean => {
  if (!coordinates || !Array.isArray(coordinates)) {
    return false;
  }

  // For Polygon: coordinates should be an array of LinearRings
  // Each LinearRing should be an array of positions (at least 4 points, first and last should be the same)
  try {
    return coordinates.every((ring) => {
      if (!Array.isArray(ring) || ring.length < 4) {
        return false;
      }

      // Check if all positions are valid [lng, lat] pairs
      const validPositions = ring.every((position) => {
        return (
          Array.isArray(position) &&
          position.length >= 2 &&
          typeof position[0] === 'number' &&
          typeof position[1] === 'number' &&
          position[0] >= -180 &&
          position[0] <= 180 &&
          position[1] >= -90 &&
          position[1] <= 90
        );
      });

      // Check if ring is closed (first and last coordinates are the same)
      const isClosed =
        ring.length >= 4 &&
        ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1];

      return validPositions && isClosed;
    });
  } catch (error) {
    console.error('Error validating coordinates:', error);
    return false;
  }
};

/**
 * Convert MultiPolygon to separate Polygon features
 */
export const convertMultiPolygonToPolygons = (multiPolygonFeature: any): any[] => {
  const { geometry, properties } = multiPolygonFeature;

  if (geometry.type !== 'MultiPolygon') {
    return [multiPolygonFeature];
  }

  // Convert each polygon in the MultiPolygon to a separate Polygon feature
  return geometry.coordinates.map((polygonCoordinates: any[], index: number) => ({
    type: 'Feature',
    properties: {
      ...properties,
      // Add an index to distinguish the polygons if needed
      _multipolygon_index: index,
    },
    geometry: {
      type: 'Polygon',
      coordinates: polygonCoordinates,
    },
  }));
};

/**
 * Process GeoJSON data for map editor
 */
export const processGeoJsonForEditor = (geoJsonData: any): any[] => {
  let featuresToProcess = [];

  if (geoJsonData.type === 'FeatureCollection') {
    // Flatten all features, converting MultiPolygons to multiple Polygons
    geoJsonData.features.forEach((feature: any) => {
      if (
        feature.geometry &&
        (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon')
      ) {
        if (feature.geometry.type === 'MultiPolygon') {
          console.log('Converting MultiPolygon to separate Polygon features...');
          const convertedFeatures = convertMultiPolygonToPolygons(feature);
          featuresToProcess.push(...convertedFeatures);
        } else {
          featuresToProcess.push(feature);
        }
      } else {
        console.warn(`Skipping feature - not a polygon:`, feature.geometry?.type);
      }
    });
  } else if (geoJsonData.type === 'Feature') {
    if (
      geoJsonData.geometry &&
      (geoJsonData.geometry.type === 'Polygon' || geoJsonData.geometry.type === 'MultiPolygon')
    ) {
      if (geoJsonData.geometry.type === 'MultiPolygon') {
        console.log('Converting single MultiPolygon to separate Polygon features...');
        const convertedFeatures = convertMultiPolygonToPolygons(geoJsonData);
        featuresToProcess.push(...convertedFeatures);
      } else {
        featuresToProcess.push(geoJsonData);
      }
    } else {
      console.warn('Feature is not a polygon:', geoJsonData.geometry?.type);
      throw new Error('Only Polygon and MultiPolygon features are supported.');
    }
  } else {
    console.warn('Unsupported GeoJSON type:', geoJsonData.type);
    throw new Error('Only Feature and FeatureCollection are supported.');
  }

  return featuresToProcess;
};
